import React, { useCallback, useEffect, useRef } from 'react';

import { Transition } from 'react-transition-group';

import { defaultTimeout, transitionEasing } from '@core/transitions/constants';
import { useReducedMotion } from '@core/transitions/hooks/useReducedMotion';
import { getTransitionTimeout } from '@core/transitions/utils';
import type { TransitionTimeout } from '@core/transitions/utils';
import { reflow, useForkRef } from '@core/utils';

import {
  getCollapseDuration,
  getCollapseStateStyles,
  normalizeCollapsedSize,
} from './utils';

/** Collapse-only: 'auto' derives duration from content height. */
export type CollapseTimeout = TransitionTimeout | 'auto';

export type Props = {
  /** When true, the content is expanded. Default false. */
  in?: boolean;
  /** Content to collapse. */
  children?: React.ReactNode;
  /**
   * By default the child does not perform the enter transition when it first mounts, regardless of `in`.
   * Set both `appear` and `in` to true to run the enter transition on initial mount.
   * @default false
   */
  appear?: boolean;
  /** Height when collapsed. Number (px) or string (e.g. '0px'). Default '0px'. */
  collapsedSize?: string | number;
  /** Duration in ms, { enter, exit }, or 'auto' (duration from content height). Uses defaultTimeout when omitted. */
  timeout?: CollapseTimeout;
  /** When true, the child is removed from the DOM after it exits. Default false. */
  unmountOnExit?: boolean;
  /** Optional CSS class name for the root element. */
  className?: string;
  style?: React.CSSProperties;
  /** Called when enter starts. Receives (node, isAppearing). */
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
  /** Called when enter has finished. Receives (node). */
  onEntered?: (node: HTMLElement) => void;
  /** Called when exit has finished. Receives (node). */
  onExited?: (node: HTMLElement) => void;
};

const Collapse = (
  props: Props,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement => {
  const {
    appear = false,
    children,
    className,
    collapsedSize: collapsedSizeProp = '0px',
    in: inProp = false,
    onEnter: onEnterProp,
    onEntered: onEnteredProp,
    onExited: onExitedProp,
    style: styleProp,
    timeout = defaultTimeout,
    unmountOnExit = false,
  } = props;

  const nodeRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const autoTransitionDuration = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitHeightRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();

  const collapsedSize = normalizeCollapsedSize(collapsedSizeProp);
  const isAutoTimeout = timeout === 'auto';
  const enterDuration =
    timeout === 'auto'
      ? 0
      : getTransitionTimeout(timeout, 'enter', defaultTimeout);
  const exitDuration =
    timeout === 'auto'
      ? 0
      : getTransitionTimeout(timeout, 'exit', defaultTimeout);
  const timeoutProp =
    typeof timeout === 'object'
      ? { enter: enterDuration, exit: exitDuration }
      : timeout;
  let effectiveTimeoutProp: number | { enter: number; exit: number } | null;
  if (reducedMotion) {
    effectiveTimeoutProp = 0;
  } else if (isAutoTimeout) {
    effectiveTimeoutProp = null;
  } else {
    effectiveTimeoutProp = timeoutProp as
      | number
      | { enter: number; exit: number };
  }
  const effectiveIsAutoTimeout = isAutoTimeout && !reducedMotion;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleOwnRef = useCallback((value: HTMLDivElement | null) => {
    nodeRef.current = value;
  }, []);
  const handleRef = useForkRef(handleOwnRef, ref);

  const handleEnter = useCallback(
    (isAppearing?: boolean) => {
      const node = nodeRef.current;
      if (node) {
        node.style.height = collapsedSize;
        reflow(node);
      }
      if (node) onEnterProp?.(node, isAppearing ?? false);
    },
    [collapsedSize, onEnterProp]
  );

  const handleEntering = useCallback(() => {
    const node = nodeRef.current;
    const wrapper = wrapperRef.current;
    if (node && wrapper) {
      // The inline style already holds height at collapsedSize (the animation start).
      // Reading clientHeight here forces a layout flush to commit that starting state,
      // then we animate to the measured content height.
      const wrapperHeight = wrapper.clientHeight;
      const duration = getCollapseDuration('enter', {
        reducedMotion,
        isAuto: effectiveIsAutoTimeout,
        measuredHeight: wrapperHeight,
        enterDuration,
        exitDuration,
      });
      if (effectiveIsAutoTimeout) {
        autoTransitionDuration.current = duration;
      }
      node.style.transition = `height ${duration}ms ${transitionEasing.standard}`;
      node.style.height = `${wrapperHeight}px`;
    }
  }, [enterDuration, exitDuration, effectiveIsAutoTimeout, reducedMotion]);

  const handleEntered = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.height = 'auto';
    }
    if (node) onEnteredProp?.(node);
  }, [onEnteredProp]);

  const handleExit = useCallback(() => {
    const node = nodeRef.current;
    const wrapper = wrapperRef.current;
    if (node && wrapper) {
      const h = wrapper.clientHeight;
      exitHeightRef.current = h;
      node.style.height = `${h}px`;
      reflow(node);
    }
  }, []);

  const handleExiting = useCallback(() => {
    const node = nodeRef.current;
    const wrapper = wrapperRef.current;
    if (node) {
      const wrapperHeight = wrapper?.clientHeight ?? 0;
      const duration = getCollapseDuration('exit', {
        reducedMotion,
        isAuto: effectiveIsAutoTimeout,
        measuredHeight: wrapperHeight,
        enterDuration,
        exitDuration,
      });
      if (effectiveIsAutoTimeout) {
        autoTransitionDuration.current = duration;
      }
      node.style.transition = `height ${duration}ms ${transitionEasing.standard}`;
      node.style.height = collapsedSize;
    }
  }, [
    collapsedSize,
    enterDuration,
    exitDuration,
    effectiveIsAutoTimeout,
    reducedMotion,
  ]);

  const handleExited = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.transition = '';
    }
    if (node) onExitedProp?.(node);
  }, [onExitedProp]);

  const isZeroCollapsed = collapsedSize === '0px';
  const transitionDurationMs = reducedMotion ? 0 : enterDuration;

  const addEndListener = useCallback(
    (next: () => void) => {
      if (effectiveIsAutoTimeout) {
        const duration = autoTransitionDuration.current || 0;
        timerRef.current = setTimeout(() => next(), duration);
      }
    },
    [effectiveIsAutoTimeout]
  );

  const transitionProps = {
    appear,
    in: inProp,
    nodeRef: nodeRef as React.RefObject<HTMLElement>,
    timeout: effectiveTimeoutProp,
    unmountOnExit,
    onEnter: handleEnter,
    onEntered: handleEntered,
    onEntering: handleEntering,
    onExit: handleExit,
    onExited: handleExited,
    onExiting: handleExiting,
    ...(effectiveIsAutoTimeout && { addEndListener }),
  };

  return (
    <Transition
      {...(transitionProps as React.ComponentProps<typeof Transition>)}
    >
      {(state) => {
        const { height, overflow, visibility } = getCollapseStateStyles(state, {
          collapsedSize,
          exitHeightPx: exitHeightRef.current,
          inProp,
          isZeroCollapsed,
        });

        return (
          <div
            ref={handleRef}
            className={className}
            style={{
              minHeight: collapsedSize,
              height,
              overflow,
              ...(!effectiveIsAutoTimeout && {
                transition: `height ${transitionDurationMs}ms ${transitionEasing.standard}`,
              }),
              ...(visibility && { visibility }),
              ...styleProp,
            }}
          >
            <div ref={wrapperRef} style={{ display: 'flex' }}>
              <div style={{ width: '100%' }}>{children}</div>
            </div>
          </div>
        );
      }}
    </Transition>
  );
};

export default React.forwardRef<HTMLDivElement, Props>(Collapse);
